import { useEffect, useState } from "react";
import type { ReactNode } from "react";

/**
 * Aufklappbare Sektion und Sprungleiste — gemeinsam für Kontor (Projektseite)
 * und Pflege (Vertragsseite), seit 09.10.2026 hier statt doppelt.
 * Layout: `@vimaana/ui/sektion` (styles/sektion.css).
 *
 * Ein Tipp in der Sprungleiste (Telefon) oder ein Aufruf von `zuSektion`
 * klappt die Sektion auf und scrollt hin. Das läuft über ein Fenster-Ereignis:
 * wer das Ziel kennt, hört zu; wer springen will, ruft nur die Funktion.
 */
export const SEKTION_EREIGNIS = "vg-sektion-oeffnen";

/** Element-ID einer Sektion mit diesem Anker. */
export function sektionId(anker: string): string {
  return `sek-${anker}`;
}

/** Sektion `anker` aufklappen und hinscrollen. */
export function zuSektion(anker: string): void {
  window.dispatchEvent(new CustomEvent(SEKTION_EREIGNIS, { detail: anker }));
  requestAnimationFrame(() => document.getElementById(sektionId(anker))?.scrollIntoView({ behavior: "smooth", block: "start" }));
}

/** Klebt am Telefon oben; auf breiten Schirmen ausgeblendet (dort steht alles im Blick). */
export function Sprungleiste({ ziele }: { ziele: { anker: string; label: string }[] }) {
  return (
    <nav className="vg-sprung" aria-label="Zu einem Abschnitt springen">
      {ziele.map(s => (
        <button key={s.anker} type="button" className="vg-sprung-knopf" onClick={() => zuSektion(s.anker)}>{s.label}</button>
      ))}
    </nav>
  );
}

export function Sektion({ titel, anker, kopfRechts, standardOffen = false, children }: {
  titel: string;
  /** Ziel der Sprungleiste und von `zuSektion`. */
  anker?: string;
  kopfRechts?: ReactNode;
  standardOffen?: boolean;
  children: ReactNode;
}) {
  const [offen, setOffen] = useState(standardOffen);
  useEffect(() => {
    if (!anker) return;
    const auf = (e: Event) => { if ((e as CustomEvent<string>).detail === anker) setOffen(true); };
    window.addEventListener(SEKTION_EREIGNIS, auf);
    return () => window.removeEventListener(SEKTION_EREIGNIS, auf);
  }, [anker]);
  return (
    <section className="vg-sektion vg-anker" id={anker ? sektionId(anker) : undefined}>
      <button type="button" className="vg-sektion-head" aria-expanded={offen} onClick={() => setOffen(o => !o)}>
        <span className="vg-sektion-chev" data-open={offen} aria-hidden="true">▸</span>
        <span className="vg-sektion-titel">{titel}</span>
        {kopfRechts && <span className="vg-sektion-rechts">{kopfRechts}</span>}
      </button>
      {offen && <div className="vg-sektion-body">{children}</div>}
    </section>
  );
}
